import { Box, Stack, Typography } from '@mui/material';

import { ProviderIcon } from '../../../icons';
import { type ProviderLinkProps, useProviderLink } from './useProviderLink';

type Props = ProviderLinkProps;

export function ProviderLink(props: Props) {
  const hook = useProviderLink(props);

  if (!hook.displayName) {
    return null;
  }

  const rootElement = props.href ? 'a' : hook.isClickable ? 'button' : 'span';

  return (
    <Stack
      component={rootElement}
      direction="row"
      spacing={0.5}
      data-testid={props['data-testid']}
      href={props.href}
      onClick={hook.isClickable ? hook.handleClick : undefined}
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'baseline',
        textDecoration: hook.underline === 'always' ? 'underline' : 'none',
        color: props.color ?? theme.palette.text.primary,
        background: 'none',
        border: 'none',
        p: 0,
        m: 0,
        font: 'inherit',
        cursor: hook.isClickable ? 'pointer' : 'inherit',
        transition: theme.transitions.create(['color'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(hook.isClickable && {
          '&:hover': {
            textDecoration: hook.underline !== 'none' ? 'underline' : 'none',
            color: props.color ?? theme.palette.primary.main,
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
            borderRadius: theme.shape.borderRadius,
          },
        }),
      })}
    >
      {!props.hideIcon && (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: 'inherit',
            fontSize: hook.isCaption ? 14 : 16,
          }}
        >
          {props.icon ?? <ProviderIcon fontSize="inherit" />}
        </Box>
      )}
      <Typography
        variant={hook.variant}
        component="span"
        noWrap={props.noWrap}
        sx={{
          color: 'inherit',
          lineHeight: 'inherit',
        }}
      >
        {hook.displayName}
      </Typography>
    </Stack>
  );
}

export type { ProviderLinkProps };
